import { deviceApi } from "#/api/deviceApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import { exportToCSV } from "#/utils/CsvUtils";
import { sanitize } from "#/utils/StringUtils";
import { Button, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconEye, IconPlus } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DeviceRegistrationDrawer from "./components/DeviceRegistrationDrawer";

const DevicesPage = () => {
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite } = usePermission();
  const { t } = useTranslation();
  const [
    createDrawerOpened,
    { open: openCreateDrawer, close: closeCreateDrawer },
  ] = useDisclosure(false);

  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const headers = [
    { key: "name", value: t("devices.columns.name") },
    { key: "is_active", value: t("devices.columns.active") },
    { key: "mobility_type", value: t("devices.columns.mobility") },
    { key: "hardware_type", value: t("devices.columns.hardware") },
    { key: "status_processing_type", value: t("devices.columns.statusProcessing") },
    { key: "Site", value: t("devices.columns.site") },
    { key: "last_seen", value: t("devices.columns.lastSeen") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const prepareExportData = (devicesToExport: typeof items) => {
    return devicesToExport.map((item) => ({
      id: item.id || "",
      name: item.name || "",
      is_active: item.is_active ? "Yes" : "No",
      mobility_type: sanitize(item.mobility_type?.toString() || ""),
      hardware_type: sanitize((item.hardware_type?.toString() || "").replace(/^chainway_/i, "")),
      status_processing_type: sanitize(item.status_processing_type?.toString() || ""),
      site: item.site?.name || "",
      last_seen: formatDateTimeForExport(item.last_seen),
    }));
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const exportHeaders = [
      "id",
      "name",
      "is_active",
      "mobility_type",
      "hardware_type",
      "status_processing_type",
      "site",
      "last_seen",
    ];

    const headerLabels = {
      id: "ID",
      name: "Name",
      is_active: "Active",
      mobility_type: "Mobility",
      hardware_type: "Hardware",
      status_processing_type: "Status Processing",
      site: "Site",
      last_seen: "Last Seen",
    };

    const note = t("common.utcNote");
    const filename = `devices-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
  };

  const handleExportAll = async () => {
    try {
      const response = await deviceApi.getAll({
        limit: total,
        page: 1,
        ...(sortBy ? { sortBy: sortBy } : {}),
        ...(sortDirection ? { order: sortDirection } : {}),
      });

      const allDevices = response.data.data;
      const exportData = prepareExportData(allDevices);

      const exportHeaders = [
        "id",
        "name",
        "is_active",
        "mobility_type",
        "hardware_type",
        "status_processing_type",
        "site",
        "last_seen",
      ];

      const headerLabels = {
        id: "ID",
        name: "Name",
        is_active: "Active",
        mobility_type: "Mobility",
        hardware_type: "Hardware",
        status_processing_type: "Status Processing",
        site: "Site",
        last_seen: "Last Seen",
      };

      const note = t("common.utcNote");
      const filename = `devices-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
    } catch (error) {
      console.error("Failed to export all devices:", error);
    }
  };

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getDevices,
  } = useQuery({
    queryKey: ["devices", sortBy, page, limit, sortDirection],
    queryFn: async () => {
      try {
        const response = await deviceApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No devices returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("devices.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          {/* <Popover
						anchor={
							<Button
								leftSection={<IconFilter size={18} />}
								variant={"outline"}
								color="black"
								size="sm"
								loading={isLoading}
							>
								Filters
							</Button>
						}
					>
						<div>sup</div>
					</Popover> */}
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                openCreateDrawer();
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("devices.registerScanner")}
            </Button>
          </PermissionTooltip>
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
            size="sm"
          >
            {t("common.exportCsv")}
          </Button>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isPending}
          allowSorting={true}
          sortableColumns={[0,1, 2, 3, 4, 5,6]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 5,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td style={{ minWidth: '180px' }}>{item.name ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '100px' }}>{item.is_active ? "Yes" : "No"}</Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '120px' }}>
                  {sanitize(item.mobility_type?.toString() ?? "--")}
                </Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '120px' }}>
                  {sanitize((item.hardware_type?.toString() ?? "--").replace(/^chainway_/i, ""))}
                </Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '160px' }}>
                  {sanitize(item.status_processing_type?.toString() ?? "--")}
                </Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '150px' }}>{item.site?.name}</Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '180px' }}>
                  {item.last_seen ? (
                    <span>
                      {formatDate(item.last_seen, client)}
                      <br />
                      {formatTime(item.last_seen, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: '100px' }}>
                  <div className="w-full flex items-center justify-center">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/devices/${item.id}`);
                      }}
                    />
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>
      <DeviceRegistrationDrawer
        opened={createDrawerOpened}
        onClose={() => {
          closeCreateDrawer();
        }}
        onSuccess={() => {
          setPage(1);
          void getDevices();
          closeCreateDrawer();
        }}
      />

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default DevicesPage;
